(() => {
  class BrandCollection extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.collectionUrl = new URL(this.dataset.collectionUrl, location.origin);
      this.status = this.querySelector('[data-brand-status]');
      this.retry = this.querySelector('[data-brand-retry]');
      this.lastUrl = new URL(location.href);
      this.prepareResults(this.lastUrl.searchParams.get('brand_query') || '', this.lastUrl);
      this.addEventListener('submit', event => this.onSubmit(event), options);
      this.addEventListener('input', event => this.onQueryInput(event), options);
      this.addEventListener('compositionstart', event => {
        if (!event.target.matches('[data-brand-query]')) return;
        this.composing = true;
        clearTimeout(this.searchTimer);
        this.controller?.abort();
      }, options);
      this.addEventListener('compositionend', event => {
        this.composing = false;
        this.onQueryInput(event);
      }, options);
      this.addEventListener('change', event => {
        if (!event.target.matches('[data-brand-stock]')) return;
        const input = [...this.querySelectorAll('.catalog-filter input')].find(input => input.name === 'filter.v.availability' && input.value === '1');
        if (input) {
          input.checked = event.target.checked;
          event.target.form.requestSubmit();
        }
      }, options);
      this.addEventListener('click', event => this.onClick(event), options);
      window.addEventListener('popstate', () => {
        clearTimeout(this.searchTimer);
        if (location.pathname === this.collectionUrl.pathname) this.load(new URL(location.href), { history: false });
      }, options);
      if (this.lastUrl.searchParams.has('brand_query') || (!this.nativeTagFilter && this.lastUrl.searchParams.has('filter.p.tag'))) this.load(this.lastUrl, { history: false });
    }

    disconnectedCallback() {
      clearTimeout(this.searchTimer);
      this.controller?.abort();
      this.events?.abort();
      this.initialized = false;
    }

    onQueryInput(event) {
      if (!event.target.matches('[data-brand-query]')) return;
      clearTimeout(this.searchTimer);
      this.controller?.abort();
      this.results.removeAttribute('aria-busy');
      this.updateClearButton();
      if (event.isComposing || this.composing) return;
      this.searchTimer = setTimeout(() => this.load(this.formUrl(), { history: this.lastUrl.searchParams.has('brand_query') ? 'replace' : 'push' }), 300);
    }

    prepareResults(query, url = this.lastUrl) {
      this.results = this.querySelector('[data-brand-results]');
      if (this.nativeVendorFilter === undefined) this.nativeVendorFilter = Boolean(this.results.querySelector('[data-brand-vendor-filter]'));
      if (this.nativeTagFilter === undefined) this.nativeTagFilter = Boolean(this.results.querySelector('[data-brand-tag-filter]'));
      this.results.querySelectorAll('[data-brand-search], [data-brand-stock-control]').forEach(control => { control.hidden = false; });
      const category = this.categoryFor(url);
      if (!this.nativeTagFilter && category) {
        // Shopify omits tag facets outside the primary locale; retain our curated category state.
        const state = document.createElement('input');
        state.type = 'hidden';
        state.name = 'filter.p.tag';
        state.value = category.dataset.brandBrowse;
        this.results.querySelector('.catalog-controls').append(state);
        const removeUrl = new URL(url);
        removeUrl.searchParams.delete('filter.p.tag');
        removeUrl.searchParams.delete('page');
        const chip = document.createElement('a');
        chip.href = removeUrl.href;
        chip.dataset.brandFilterValue = state.value;
        const label = document.createElement('span');
        label.textContent = category.textContent.trim();
        chip.append(label);
        const icon = this.results.querySelector('[data-brand-search-clear] .icon');
        if (icon) chip.append(icon.cloneNode(true));
        this.results.querySelector('.active-filters')?.append(chip);
      }
      const input = this.results.querySelector('[data-brand-query]');
      input.value = query;
      const label = this.results.querySelector('label[for="' + CSS.escape(input.id) + '"]');
      // Search fragments use their own section ID; keep the brand-specific label from the initial form.
      this.searchLabel ||= input.placeholder;
      input.placeholder = this.searchLabel;
      if (label) label.textContent = this.searchLabel;
      const submit = this.results.querySelector('.brand-collection__submit');
      submit?.setAttribute('aria-label', this.searchLabel);
      submit?.setAttribute('title', this.searchLabel);
      const activeTags = [...this.results.querySelectorAll('[name="filter.p.tag"]')].map(input => input.value);
      const sort = this.results.querySelector('[data-sort]')?.value;
      this.querySelectorAll('[data-brand-navigation] a').forEach(link => {
        const active = link.hasAttribute('data-brand-sort') ? !activeTags.length && sort === link.dataset.brandSort
          : link.dataset.brandBrowse ? activeTags.includes(link.dataset.brandBrowse)
          : !activeTags.length && !['best-selling', 'created-descending'].includes(sort);
        if (active) link.setAttribute('aria-current', 'page');
        else link.removeAttribute('aria-current');
      });
      this.results.querySelectorAll('[data-brand-filter-value]').forEach(chip => {
        const category = [...this.querySelectorAll('[data-brand-navigation] [data-brand-browse]')].find(link => link.dataset.brandBrowse === chip.dataset.brandFilterValue);
        if (category) {
          chip.querySelector('span').textContent = category.textContent.trim();
          chip.setAttribute('aria-label', category.textContent.trim());
        }
      });
      this.querySelector('brand-saves')?.refresh?.();
      this.results.querySelectorAll('[data-filter-panel]').forEach(panel => { panel.hidden = true; });
      document.querySelector('quick-shop')?.enableButtons?.(this.results);
      this.updateClearButton();
    }

    updateClearButton() {
      this.querySelector('[data-brand-search-clear]').hidden = !this.querySelector('[data-brand-query]').value;
    }

    categoryFor(url) {
      const tag = url.searchParams.get('filter.p.tag');
      return tag ? [...this.querySelectorAll('[data-brand-navigation] [data-brand-category-url]')].find(link => link.dataset.brandBrowse === tag) : null;
    }

    formUrl() {
      const url = new URL(this.collectionUrl);
      const form = this.querySelector('.catalog-controls');
      const values = new FormData(form);
      for (const [name, value] of values) {
        if ((name.startsWith('filter.') || name === 'sort_by' || name === 'brand_query') && value) url.searchParams.append(name, value);
      }
      const preview = new URL(location.href).searchParams.get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      return url;
    }

    onSubmit(event) {
      if (!event.target.matches('.catalog-controls')) return;
      event.preventDefault();
      clearTimeout(this.searchTimer);
      if (this.composing) return;
      this.load(this.formUrl());
    }

    onClick(event) {
      const browse = event.target.closest('[data-brand-browse], [data-brand-sort]');
      if (browse && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
        if (browse.hasAttribute('data-brand-browse') && browse.dataset.brandBrowse && !this.nativeTagFilter) {
          const category = [...this.querySelectorAll('[data-brand-category-url]')].find(link => link.dataset.brandBrowse === browse.dataset.brandBrowse);
          if (!category) return;
        }
        event.preventDefault();
        clearTimeout(this.searchTimer);
        const url = browse.hasAttribute('data-brand-sort') ? new URL(this.collectionUrl) : this.formUrl();
        url.searchParams.delete('filter.p.tag');
        if (browse.dataset.brandBrowse) url.searchParams.set('filter.p.tag', browse.dataset.brandBrowse);
        if (browse.dataset.brandSort) url.searchParams.set('sort_by', browse.dataset.brandSort);
        const preview = new URL(location.href).searchParams.get('preview_theme_id');
        if (preview) url.searchParams.set('preview_theme_id', preview);
        this.load(url, { scroll: true });
        return;
      }
      if (event.target.closest('[data-brand-search-clear]')) {
        this.querySelector('[data-brand-query]').value = '';
        clearTimeout(this.searchTimer);
        this.load(this.formUrl());
        return;
      }
      if (event.target.closest('[data-brand-reset]')) {
        clearTimeout(this.searchTimer);
        const url = new URL(this.collectionUrl);
        const preview = new URL(location.href).searchParams.get('preview_theme_id');
        if (preview) url.searchParams.set('preview_theme_id', preview);
        this.load(url);
        return;
      }
      if (event.target.closest('[data-brand-retry]')) {
        this.load(this.failedUrl);
        return;
      }
      const link = event.target.closest('.pagination a, .active-filters a, .catalog-filters__submit a');
      if (!link || !this.results.contains(link) || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const source = new URL(link.href);
      if (source.origin !== location.origin) return;
      event.preventDefault();
      const url = new URL(this.collectionUrl);
      for (const [name, value] of source.searchParams) {
        if (name.startsWith('filter.') || name === 'sort_by' || name === 'page' || name === 'preview_theme_id') url.searchParams.append(name, value);
      }
      const query = this.querySelector('[data-brand-query]').value.trim();
      if (query) url.searchParams.set('brand_query', query);
      if (!this.nativeTagFilter && this.categoryFor(this.lastUrl) && (link.closest('.pagination') || (link.closest('.active-filters') && !link.hasAttribute('data-brand-filter-value')))) {
        url.searchParams.set('filter.p.tag', this.lastUrl.searchParams.get('filter.p.tag'));
      }
      const preview = new URL(location.href).searchParams.get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      this.load(url, { scroll: Boolean(link.closest('.pagination')) });
    }

    requestUrl(url) {
      const query = (url.searchParams.get('brand_query') || '').trim().slice(0, 80);
      const category = this.categoryFor(url);
      if (!query) {
        url.searchParams.delete('brand_query');
        if (url.searchParams.get('sort_by') === 'relevance') url.searchParams.delete('sort_by');
        const request = new URL(url);
        if (!this.nativeTagFilter && category) {
          request.pathname = new URL(category.dataset.brandCategoryUrl, location.origin).pathname;
          request.searchParams.delete('filter.p.tag');
          request.searchParams.set('filter.p.vendor', this.dataset.vendor);
        }
        request.searchParams.set('section_id', !this.nativeTagFilter && category ? 'brand-catalog-results' : this.dataset.sectionId);
        return { request, query };
      }
      url.searchParams.set('brand_query', query);
      const request = new URL(this.dataset.searchUrl, location.origin);
      for (const [name, value] of url.searchParams) {
        if (name.startsWith('filter.') || name === 'sort_by' || name === 'page' || name === 'preview_theme_id') request.searchParams.append(name, value);
      }
      if (!['relevance', 'price-ascending', 'price-descending'].includes(request.searchParams.get('sort_by'))) {
        request.searchParams.set('sort_by', 'relevance');
        url.searchParams.set('sort_by', 'relevance');
      }
      if (this.nativeVendorFilter) {
        // Keep the brand boundary in a facet so Shopify can retain partial-word and typo matching.
        request.searchParams.set('q', query);
        request.searchParams.set('filter.p.vendor', this.dataset.vendor);
      } else {
        const terms = query.replace(/["\\()]/g, ' ').split(/\s+/).filter(Boolean).map(term => JSON.stringify(term));
        const scope = `vendor:${JSON.stringify(this.dataset.vendor)}`;
        request.searchParams.set('q', terms.length ? `${scope} AND (${terms.join(' AND ')})` : scope);
      }
      if (!this.nativeTagFilter && category) {
        const terms = query.replace(/["\\():]/g, ' ').trim();
        request.searchParams.delete('filter.p.tag');
        const tagScope = `tag:${JSON.stringify(category.dataset.brandBrowse)}`;
        const vendorScope = this.nativeVendorFilter ? '' : `vendor:${JSON.stringify(this.dataset.vendor)} AND `;
        request.searchParams.set('q', terms ? `${vendorScope}${tagScope} AND (${terms})` : `${vendorScope}${tagScope}`);
      }
      request.searchParams.set('type', 'product');
      request.searchParams.set('options[prefix]', 'last');
      request.searchParams.set('options[unavailable_products]', 'show');
      request.searchParams.set('section_id', 'brand-catalog-results');
      return { request, query };
    }

    async load(source, { history: historyMode = 'push', scroll = false } = {}) {
      if (!source) return;
      this.controller?.abort();
      const controller = new AbortController();
      this.controller = controller;
      const url = new URL(source);
      const { request, query } = this.requestUrl(url);
      const focused = this.results.contains(document.activeElement) ? document.activeElement : null;
      const focusName = focused?.name;
      const focusSelector = focused?.matches('[data-brand-stock]') ? '[data-brand-stock]'
        : focused?.closest('.brand-price') ? '.brand-price summary'
        : focused?.closest('.catalog-filters') || focused?.matches('[data-filter-toggle]') ? '[data-filter-toggle]'
        : focused?.matches('[data-brand-search-clear], [data-brand-reset]') ? '[data-brand-query]' : null;
      const selection = focused?.matches('[data-brand-query]') ? [focused.selectionStart, focused.selectionEnd] : null;
      this.results.setAttribute('aria-busy', 'true');
      this.status.classList.remove('visually-hidden');
      this.status.textContent = this.dataset.loading;
      this.retry.hidden = true;
      try {
        const response = await fetch(request, { signal: controller.signal, headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error('Brand catalog request failed');
        const page = new DOMParser().parseFromString(await response.text(), 'text/html');
        const result = page.querySelector('[data-brand-results]');
        if (!result) throw new Error('Brand catalog results missing');
        if (controller.signal.aborted) return;
        this.results.replaceWith(result);
        this.prepareResults(query, url);
        this.lastUrl = url;
        this.failedUrl = null;
        if (historyMode) window.history[historyMode === 'replace' ? 'replaceState' : 'pushState']({}, '', url);
        if (focusName || focusSelector) {
          const replacement = focusSelector ? this.results.querySelector(focusSelector)
            : [...this.results.querySelectorAll('[name]')].find(input => input.name === focusName);
          replacement?.focus({ preventScroll: true });
          if (selection && replacement?.setSelectionRange) replacement.setSelectionRange(...selection);
        }
        if (scroll) {
          this.results.focus({ preventScroll: true });
          this.results.scrollIntoView({ block: 'start', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
        }
        this.status.textContent = this.results.querySelector('.catalog-controls__row > p').textContent.trim();
        this.status.classList.add('visually-hidden');
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.failedUrl = url;
          this.status.textContent = this.dataset.error;
          this.retry.hidden = false;
        }
      } finally {
        if (this.controller === controller) this.results.removeAttribute('aria-busy');
      }
    }
  }
  if (!customElements.get('brand-collection')) customElements.define('brand-collection', BrandCollection);
})();
